import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Caja } from 'app/models/caja.models';
import { CashSessionCloseResponse, PaymentMethodBalance } from 'app/models/cash-session-close.models';
import { CashBoxSession } from 'app/models/cashBoxSession.models';
import { GeneralesService } from 'app/services/generales.service';

@Component({
  selector: 'app-cierre',
  templateUrl: './cierre.component.html',
  styleUrls: ['./cierre.component.css']
})
export class CierreComponent implements OnInit {

  montoCtrl: UntypedFormControl = new UntypedFormControl('');
  
  // Datos dinámicos basados en la respuesta de la API
  cashCloseData: CashSessionCloseResponse;
  paymentMethods: PaymentMethodBalance[] = [];
  
  // Datos calculados
  saldoCajaTotal = 0;
  totalCompra = 0;
  montoTotal = 0;
  montoApertura = 0;
  totalEgreso = 0;
  fechaApertura: Date;
  montoCierre = 0;
  cajaActual: Caja;
  usuarioActual: string;
  cashSession: CashBoxSession;

  constructor(public dialogRef: MatDialogRef<CierreComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private servicioGrales: GeneralesService,) {
    this.cajaActual = data.cajaActual;
    this.cashSession = data.cashSessionCloseData.cashSession;
  }

  ngOnInit(): void {
    // Si recibimos datos en el formato anterior, los procesamos
    if (this.data.cashSessionCloseData) {
      this.cashCloseData = this.data.cashSessionCloseData;
      this.processCloseData();
    } else {
      // Compatibilidad con formato anterior
      this.processLegacyData();
    }
  }

  private processCloseData(): void {
    if (!this.cashCloseData) return;

    // Configurar datos básicos
    this.montoApertura = this.cashCloseData.balance.openingBalance || 0;
    this.montoTotal = this.cashCloseData.balance.totalIncome || 0;
    this.totalCompra = this.cashCloseData.balance.totalExpenses || 0;
    this.saldoCajaTotal = this.cashCloseData.balance.totalCash || 0;
    this.fechaApertura = new Date(this.cashCloseData.cashSession.openTime);
    this.usuarioActual = this.cashCloseData.cashSession.user.name;
    this.totalEgreso = this.cashCloseData.balance.totalExpenses || 0;
    // Procesar medios de pago
    this.paymentMethods = this.cashCloseData.paymentMethodsBalance || [];
  }

  private processLegacyData(): void {
    // Mantener compatibilidad con formato anterior
    this.montoApertura = this.data.montoApertura || 0;
    this.montoCierre = this.data.montoCierre || 0;
    this.totalCompra = this.data.totalCompra || 0;
    this.montoTotal = this.data.montoTotal || 0;
    this.fechaApertura = this.data.fechaApertura ? new Date(this.data.fechaApertura) : new Date();
    this.totalEgreso = this.data.totalExpenses || 0;
    this.saldoCajaTotal = this.data.saldoCajaTotal || 0;

    // Crear medios de pago desde datos legacy
    this.paymentMethods = [];
    
    if (this.data.efectivo || this.data.efectivoPago) {
      this.paymentMethods.push({
        paymentMethodId: 1,
        paymentMethodName: 'Efectivo',
        paymentMethodCode: '1',
        totalAmount: this.data.efectivo || 0,
        transactionCount: 1
      });
    }
    
    if (this.data.credito || this.data.creditoPago) {
      this.paymentMethods.push({
        paymentMethodId: 2,
        paymentMethodName: 'Tarjeta de Crédito',
        paymentMethodCode: '2',
        totalAmount: this.data.credito || 0,
        transactionCount: 1
      });
    }
    
    if (this.data.debito || this.data.debitoPago) {
      this.paymentMethods.push({
        paymentMethodId: 3,
        paymentMethodName: 'Tarjeta de Débito',
        paymentMethodCode: '3',
        totalAmount: this.data.debito || 0,
        transactionCount: 1
      });
    }
    
    if (this.data.cheque || this.data.chequePago) {
      this.paymentMethods.push({
        paymentMethodId: 4,
        paymentMethodName: 'Cheque',
        paymentMethodCode: '4',
        totalAmount: this.data.cheque || 0,
        transactionCount: 1
      });
    }
    
    if (this.data.transferencia || this.data.transferenciaPago) {
      this.paymentMethods.push({
        paymentMethodId: 5,
        paymentMethodName: 'Transferencia',
        paymentMethodCode: '5',
        totalAmount: this.data.transferencia || 0,
        transactionCount: 1
      });
    }
  }


  guardarCaja() {
    this.servicioGrales.cerrarCaja(this.cashSession).subscribe(
      data => {
        this.dialogRef.close(data['message']);
      },
      err => {
        console.error('Error al cerrar la caja:', err);
        this.dialogRef.close({
          success: false,
          message: 'Error al cerrar la caja. Intente nuevamente.'
        });
      }
    )
  }

  cancelar() {
    this.dialogRef.close();
  }

  imprimir() {
    this.generatePrintContent();
  }

  private generatePrintContent(): void {
    const printContent = this.createPrintHTML();
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Esperar a que se cargue el contenido antes de imprimir
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }

  private createPrintHTML(): string {
    const fechaActual = new Date();
    const fechaImpresion = fechaActual.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const horaImpresion = fechaActual.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Cierre de Caja - ${this.cajaActual?.salesPoint?.name || 'Caja'}</title>
        <style>
          @media print {
            body { 
              margin: 0; 
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.3;
            }
            @page { 
              margin: 15mm; 
              size: A4;
            }
          }
          
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.3;
            color: #000;
            max-width: 210mm;
            margin: 0 auto;
            padding: 10px;
          }
          
          .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          
          .header h1 {
            margin: 0;
            font-size: 18px;
            font-weight: bold;
          }
          
          .header h2 {
            margin: 5px 0;
            font-size: 14px;
            font-weight: normal;
          }
          
          .info-section {
            margin-bottom: 20px;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          
          .info-label {
            font-weight: bold;
          }
          
          .separator {
            border-top: 1px solid #000;
            margin: 15px 0;
          }
          
          .table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
          }
          
          .table th,
          .table td {
            border: 1px solid #000;
            padding: 5px;
            text-align: left;
          }
          
          .table th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
          }
          
          .text-right {
            text-align: right !important;
          }
          
          .totals-row {
            font-weight: bold;
            background-color: #f8f8f8;
          }
          
          .balance-section {
            text-align: center;
            margin: 20px 0;
            padding: 10px;
            border: 2px solid #000;
            background-color: #f0f0f0;
          }
          
          .balance-title {
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .balance-amount {
            font-size: 16px;
            font-weight: bold;
          }
          
          .summary-section {
            margin-top: 20px;
          }
          
          .summary-title {
            font-weight: bold;
            text-align: center;
            margin-bottom: 10px;
            text-decoration: underline;
          }
          
          .summary-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .summary-card {
            border: 1px solid #000;
            padding: 10px;
          }
          
          .card-title {
            font-weight: bold;
            text-align: center;
            margin-bottom: 8px;
            text-decoration: underline;
          }
          
          .summary-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          
          .payment-detail {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            border-top: 1px solid #000;
            padding-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CIERRE DE CAJA</h1>
          <h2>${this.cajaActual?.salesPoint?.name || 'Sistema de Caja'}</h2>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Caja:</span>
            <span>${this.cajaActual?.salesPoint?.name || '-'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Fecha Apertura:</span>
            <span>${this.fechaApertura ? this.fechaApertura.toLocaleDateString('es-ES') : 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Hora Apertura:</span>
            <span>${this.fechaApertura ? this.fechaApertura.toLocaleTimeString('es-ES') : 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Usuario:</span>
            <span>${this.usuarioActual || '-'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Fecha Impresión:</span>
            <span>${fechaImpresion} ${horaImpresion}</span>
          </div>
        </div>
        
        <div class="separator"></div>
        
        <h3 class="summary-title">ARQUEO DE CAJA</h3>
        
        <table class="table">
          <thead>
            <tr>
              <th>DESCRIPCIÓN</th>
              <th>EGRESOS</th>
              <th>INGRESOS</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>CAMBIO APERTURA</td>
              <td class="text-right">-</td>
              <td class="text-right">$ ${this.formatNumber(this.montoApertura)}</td>
            </tr>
            <tr>
              <td>DINERO EXTRAIDO</td>
              <td class="text-right">$ ${this.formatNumber(this.totalEgreso)}</td>
              <td class="text-right">-</td>
            </tr>
            ${this.paymentMethods.map(method => `
            <tr>
              <td>${method.paymentMethodName.toUpperCase()}</td>
              <td class="text-right">$ 0.00</td>
              <td class="text-right">$ ${this.formatNumber(method.totalAmount)}</td>
            </tr>
            `).join('')}
            <tr class="totals-row">
              <td><strong>TOTALES</strong></td>
              <td class="text-right"><strong>$ ${this.formatNumber(this.totalCompra)}</strong></td>
              <td class="text-right"><strong>$ ${this.formatNumber(this.montoTotal)}</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div class="balance-section">
          <div class="balance-title">TOTAL EFECTIVO EN CAJA</div>
          <div class="balance-amount">${this.formatCurrency(this.saldoCajaTotal)}</div>
        </div>
        
        ${this.cashCloseData ? `
        <div class="summary-section">
          <h3 class="summary-title">RESUMEN DE TRANSACCIONES</h3>
          
          <div class="summary-grid">
            <div class="summary-card">
              <div class="card-title">Operaciones</div>
              <div class="summary-item">
                <span>Total operaciones:</span>
                <span>${this.cashCloseData.operationsCount || 0}</span>
              </div>
              <div class="summary-item">
                <span>Métodos utilizados:</span>
                <span>${this.cashCloseData.summary?.paymentMethodsCount || 0}</span>
              </div>
              <div class="summary-item">
                <span>Total extracciones:</span>
                <span>${this.cashCloseData.balance?.totalWithdrawals || 0}</span>
              </div>
            </div>
            
            <div class="summary-card">
              <div class="card-title">Detalle por Método</div>
              ${this.paymentMethods.map(method => `
              <div class="payment-detail">
                <span>${method.paymentMethodName}:</span>
                <span>${method.transactionCount || 0}</span>
              </div>
              `).join('')}
            </div>
          </div>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Documento generado automáticamente por el Sistema ERP</p>
          <p>Fecha y hora de impresión: ${fechaImpresion} ${horaImpresion}</p>
        </div>
      </body>
      </html>
    `;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value || 0);
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  }

}
